import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';

/**
 * CSF (Creator Storefront) Adobe Analytics `pageName` mapping. Single source of
 * truth for the `creatorstorefront:<section>:<world>:*` value reported to Adobe
 * SiteCatalyst and used for activity-map attribution — changing any string
 * here rewrites historical reporting keys, so treat this map as an external
 * API contract, not implementation detail.
 */
const CSF_PAGE_NAME_MAP = {
    [CSF_PAGE_TYPES.FEATURED]: 'creatorstorefront:landing:n/a:*',
    [CSF_PAGE_TYPES.PRODUCTS]: 'creatorstorefront:product:n/a:*',
    [CSF_PAGE_TYPES.COLLECTIONS]: 'creatorstorefront:collections:n/a:*',
    [CSF_PAGE_TYPES.POSTS]: 'creatorstorefront:posts:n/a:*',
    [CSF_PAGE_TYPES.POST]: 'creatorstorefront:posts:detail:*',
    [CSF_PAGE_TYPES.COLLECTION]: 'creatorstorefront:collections:detail:*'
};

/**
 * Look up the Adobe `pageName` for a given CSF page type. Falls back to the
 * FEATURED (landing) pageName for unknown values so the analytics stream never
 * carries an undefined pageName.
 * @param {string} pageType One of `CSF_PAGE_TYPES`.
 * @returns {string}
 */
export const getCSFPageName = pageType => {
    return CSF_PAGE_NAME_MAP[pageType] || CSF_PAGE_NAME_MAP[CSF_PAGE_TYPES.FEATURED];
};

/**
 * Mutate `digitalData` to reflect the CSF pageName for the given page type.
 * Writes both `page.pageInfo.pageName` (consumed by legacy analytics readers)
 * and `page.attributes.sephoraPageInfo.pageName` (Sephora-scoped Adobe field).
 *
 * No-ops silently in three cases:
 *   1. SSR — `window` is undefined (server-side render pipeline).
 *   2. `window.digitalData` is undefined (analytics data layer not yet
 *      initialized on this document).
 *   3. Either target object (`page.pageInfo` or `page.attributes.sephoraPageInfo`)
 *      is absent on an otherwise-initialized `digitalData` — the corresponding
 *      write is skipped independently.
 * @param {string} pageType One of `CSF_PAGE_TYPES`.
 */
export const setDigitalDataPageInfo = pageType => {
    /* istanbul ignore next -- SSR-only guard, not exercised under the
       jsdom-based test environment used by this suite. */
    if (typeof window === 'undefined') {
        return;
    }

    const digitalData = window.digitalData;
    const pageName = getCSFPageName(pageType);

    if (digitalData?.page?.attributes?.sephoraPageInfo) {
        digitalData.page.attributes.sephoraPageInfo.pageName = pageName;
    }

    if (digitalData?.page?.pageInfo) {
        digitalData.page.pageInfo.pageName = pageName;
    }
};
